// Deep-link d'une notification → écran de l'app (CCT-97, CCT-131).
//
// Partagé entre le centre de notifications (clic sur une carte) et l'ouverture d'une
// NOTIFICATION NAVIGATEUR (message `cc-push-open` du service worker) : les deux doivent mener
// au même endroit. Toute route émise par le moteur (`_ROUTE` / `_chapter_route` /
// `_us_chat_route_for` de cc-attack-backend/business/mission_engine.py) doit être traitée ICI,
// sinon le clic échoue en silence.
//
// ── RÈGLE UNIQUE (CCT-131) ────────────────────────────────────────────────────────────────
// Toute notification RATTACHÉE À UNE MISSION ouvre `ChapterDetail` — le seul écran qui porte à
// la fois l'histoire et le context de mission (progression, équipe) — avec une INTENTION :
//   • `openItemId`       : élément précis (scène, chat scénarisé, carte, quiz, US) ;
//   • `usChatElementId`  : chat du MOTEUR attaché à une US ;
//   • `openFirstChat`    : « une discussion vous attend », sans élément désigné ;
//   • `openFirstPending` : aucune cible précise → 1er élément non terminé du chapitre.
//
// Avant cette règle, la même notification tombait tantôt sur un écran autonome (MissionChat,
// TaskDetail), tantôt sur la timeline, selon la FORME de sa route : d'où l'impression d'un
// comportement aléatoire (« parfois un chat, parfois une US, parfois la timeline »). Les routes
// HÉRITÉES — encore nombreuses en base et jamais ré-émises, les beats étant dédoublonnés — sont
// normalisées ICI, pas côté moteur.
//
// CCT-133 : la DÉCISION est déléguée au RESOLVER (`notificationTarget`), qui tranche à partir
// de ce qui EXISTE dans la mission (élément présent ? chapitre présent ?) et non de la syntaxe
// de la route. Ce module ne fait plus que brancher le store de scénarios et naviguer.
//
// Contrat verrouillé par `e2e/notification-routing.test.mjs` (table route → écran + intention).
// ⚠️ Resolver et navigation vivent dans LE MÊME module : un import entre deux fichiers .ts
// obligerait le test (Node, ESM strict) à écrire l'extension, que les bundlers de l'app ne
// résolvent pas de la même façon. Un seul fichier = un seul contrat, testable tel quel.

/**
 * Do two deep-links designate THE SAME target? — CCT-345.
 *
 * The query string is dropped deliberately: the engine enriches its routes with the carrying
 * chapter (`?c=2`) while the screen only knows its own task. Comparing raw strings would never
 * match — a guard that simply stops firing.
 *
 * ⚠️ TWIN IN `web/sw.js`. The service worker is served AS IS (no bundler ever walks it) and
 * therefore cannot import this module: it carries a copy, flagged on both sides. Letting them
 * diverge means a browser notification popping up again while the conversation is open on
 * screen — or the opposite, a notification lost.
 */
export function sameRoute(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const nu = (r: string) => String(r).split('?')[0].replace(/\/+$/, '').toLowerCase();
  return nu(a) === nu(b);
}

/** Vue MINIMALE du scénario dont le resolver a besoin (le store en fournit bien plus). */
export interface ResolverScenario {
  chapters?: { id?: string | number; sequence?: { id?: string | number; type?: string }[] }[];
}

export interface NotificationTarget {
  /** Écran cible. Toute notification rattachée à une mission ouvre `ChapterDetail`. */
  screen: 'ChapterDetail' | 'Chat' | 'Dashboard' | 'CompanyDetail' | 'Invite'
        | 'MissionCards' | 'Quiz' | 'SceneReader';
  params: Record<string, any>;
  /** Diagnostic : la route désignait un élément ABSENT du scénario chargé (US orpheline,
   *  storyline modifiée depuis l'envoi). L'écran peut le dire au lieu de faire semblant. */
  missing?: boolean;
}

const isChapterId = (id: string) => /^ch[-_]/i.test(id);

/** Rang 1-based du chapitre portant `elementId`, 0 si introuvable. */
function locate(scenario: ResolverScenario | null | undefined, elementId: string): number {
  const chs = scenario?.chapters ?? [];
  for (let i = 0; i < chs.length; i++) {
    if ((chs[i].sequence ?? []).some((it) => String(it.id) === elementId)) return i + 1;
  }
  return 0;
}

/** Rang 1-based d'un chapitre par son id, 0 si introuvable. */
function locateChapter(scenario: ResolverScenario | null | undefined, chapterId: string): number {
  const idx = (scenario?.chapters ?? []).findIndex((c) => String(c.id) === chapterId);
  return idx >= 0 ? idx + 1 : 0;
}

/** Découpe la route en (verbe, missionId, id éventuel, rang `?c=`). */
function parse(route: string) {
  const q = route.match(/[?&]c=(\d+)/);
  const rank = q ? Number(q[1]) : 0;
  const m = route.match(/^\/(chapter|us-chat|mission-chat|user-story|task|mission)(?:\/(\d+))?(?:\/(?:chapters\/)?([^/?]+))?/);
  return {
    verb: m?.[1] ?? '',
    missionId: m?.[2] ? Number(m[2]) : 0,
    id: m?.[3] ? decodeURIComponent(m[3]) : '',
    rank,
  };
}

/**
 * Cible d'une notification. `scenario` = scénario DÉJÀ chargé de la mission (ou `null`).
 * Ne dépend d'aucun état global : c'est l'appelant qui fournit le scénario.
 */
export function resolveNotificationTarget(
  route?: string | null,
  scenario?: ResolverScenario | null,
): NotificationTarget | null {
  if (!route) return null;

  const company = route.match(/^\/company\/(\d+)(?:\?tab=(\w+))?/);
  if (company) {
    return { screen: 'CompanyDetail', params: { companyId: Number(company[1]), tab: company[2] || 'members' } };
  }
  if (route.startsWith('/invite/')) {
    return { screen: 'Invite', params: { token: route.slice('/invite/'.length) } };
  }

  const { verb, missionId, id, rank } = parse(route);

  // Routes NUES héritées : aucun id de mission à exploiter.
  if (verb && !missionId) {
    if (verb === 'mission-chat') return { screen: 'Chat', params: {} };
    if (verb === 'user-story' || verb === 'task') return { screen: 'Dashboard', params: {} };
  }

  if (missionId) {
    const base: Record<string, any> = { missionId };
    // Le chat du MOTEUR est un cas à part : il ne vit pas dans la séquence, il s'ouvre par
    // l'id de l'US. Inutile de le chercher dans le scénario.
    if (verb === 'us-chat' && id) {
      return { screen: 'ChapterDetail', params: { ...base, usChatElementId: id, ...(rank ? { chapterId: rank } : {}) } };
    }

    if (id && !isChapterId(id)) {
      const at = locate(scenario, id);
      if (at) return { screen: 'ChapterDetail', params: { ...base, chapterId: at, openItemId: id } };
      // Scénario absent : on garde l'intention, l'écran résoudra au chargement du blob.
      if (!scenario) {
        return { screen: 'ChapterDetail', params: { ...base, openItemId: id, ...(rank ? { chapterId: rank } : {}) } };
      }
      // Scénario chargé ET élément introuvable : US orpheline ou storyline modifiée.
      return { screen: 'ChapterDetail', params: { ...base, openFirstPending: true }, missing: true };
    }

    if (id && isChapterId(id)) {
      const at = locateChapter(scenario, id);
      // Une annonce de chat vise la 1re DISCUSSION du chapitre ; sinon la 1re étape à faire.
      const intent = verb === 'mission-chat' ? { openFirstChat: true } : { openFirstPending: true };
      return {
        screen: 'ChapterDetail',
        params: { ...base, storyChapterId: id, ...(at ? { chapterId: at } : {}), ...intent },
      };
    }

    // Aucune cible précise (ex. `/mission/21`, `/user-story/21`).
    const intent = verb === 'mission-chat' ? { openFirstChat: true } : { openFirstPending: true };
    return { screen: 'ChapterDetail', params: { ...base, ...(rank ? { chapterId: rank } : {}), ...intent } };
  }

  // Écrans autonomes hérités (pas d'id de mission dans la route).
  const item = route.match(/^\/(mission-cards|quiz|scene-reader)(?:\/([^/?]+))?/);
  if (item) {
    const screen = ({ 'mission-cards': 'MissionCards', quiz: 'Quiz', 'scene-reader': 'SceneReader' } as const)[
      item[1] as 'mission-cards' | 'quiz' | 'scene-reader'];
    return { screen, params: { itemId: item[2] } };
  }

  return null;
}


type Nav = { navigate: (name: any, params?: any) => void };

/** Scénario de la mission déjà chargé (store runtime) ; `null` si pas encore là. */
type ScenarioLookup = (missionId: number) => ResolverScenario | null | undefined;

let lookupScenario: ScenarioLookup = () => null;
/** Injecté par l'app au démarrage : le resolver reste PUR et testable sans le store. */
export function setScenarioLookup(fn: ScenarioLookup): void { lookupScenario = fn; }

export function navigateNotificationRoute(nav: Nav, route?: string | null): boolean {
  const mid = Number(String(route || '').match(/\/(\d+)(?:[/?]|$)/)?.[1] ?? 0);
  const target = resolveNotificationTarget(route, mid ? lookupScenario(mid) : null);
  if (!target) return false;
  nav.navigate(target.screen, { ...target.params, ...(target.missing ? { deepItemMissing: true } : {}) });
  return true;
}
